import * as React from "react";
import { UI } from "../../../../core/ui/index";
import { logger } from "@lib/common";
import { Link } from "../../../components/router/Link";
import { validators } from "@lib/common";
import { MobxComponent } from "../../../../mobx/components";
import { inject, observer } from "mobx-react";
import { FaEnvelope } from "react-icons/fa";
import { PasswordInput } from "../components/password-input";
import {
	FormField,
	FormInput,
	FormLabel,
	FormStack,
	FooterLinks,
	InputWrap,
	LeadingIcon,
	SubmitButton,
	TestEnvLabel,
} from "./login.form.c";

interface Props {
	load: (load: boolean) => void;
	loading: boolean;
}
interface State {
	email?: string;
	password?: string;
}

@inject("store") @observer
export class AuthLoginForm extends MobxComponent<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			email: "",
			password: "",
		};
	}

	onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const { store } = this.injected;
		try {
			let email = this.state.email;
			const password = this.state.password;

			if (!email || !password) {
				return UI.notification.error("Please fill out all the fields");
			}

			const email_valid = validators.email(email);
			if (email_valid.error) {
				return UI.notification.error("Invalid e-mail address");
			}

			email = email_valid.value;
			this.props.load(true);
			const data = await store.api.user_login({ email, password });

			if (data.outcome) {
				this.props.load(false);
				return UI.notification.error(data.message);
			}
			this.props.load(false);

			const loginPath = store.router.s.query.redirect || "/";
			await store.service.login(data.token, loginPath);

		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error("Something went wrong, try again or contact us", { timeout: 6000 });
			this.props.load(false);
		}
		return null;
	}

	onChange = <T extends keyof State>(e: React.ChangeEvent<HTMLInputElement>) => {
		const newState = UI.helpers.handleChange(e);
		this.setState(newState as { [P in T]: State[P]; });
	}

	render() {
		const { onSubmit, onChange } = this;
		const { loading } = this.props;
		const resellerId = this.injected.store.reseller?._id;
		const testResellerLabel = resellerId && ["cloudwaitress-test", "cloudwaitress-test-ca", "cloudwaitress-test-us"].indexOf(resellerId) > -1 ? resellerId : "";

		return (
			<form onSubmit={onSubmit}>
				<FormStack>
					<FormField>
						<FormLabel htmlFor="email">Email Address</FormLabel>
						<InputWrap>
							<LeadingIcon aria-hidden="true">
								<FaEnvelope />
							</LeadingIcon>
							<FormInput
								id="email"
								type="email"
								name="email"
								placeholder="you@example.com"
								value={this.state.email}
								onChange={onChange}
								autoComplete="email"
								required={true}
								disabled={loading}
								tabIndex={1}
								hasLeadingIcon={true}
							/>
						</InputWrap>
					</FormField>

					<FormField>
						<FormLabel htmlFor="password">Password</FormLabel>
						<PasswordInput
							id="password"
							name="password"
							placeholder="Enter your password"
							value={this.state.password || ""}
							onChange={onChange}
							autoComplete="current-password"
							required={true}
							disabled={loading}
							tabIndex={2}
						/>
					</FormField>

					<SubmitButton type="submit" disabled={loading} tabIndex={3}>
						{loading ? (
							<>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
									<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
									<path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" opacity="0.75" />
								</svg>
								Signing in…
							</>
						) : (
							"Login"
						)}
					</SubmitButton>

					<FooterLinks>
						<p>
							<span>Don&apos;t have an account?</span>
							<Link to="/register" tabIndex={4}>Sign Up</Link>
						</p>
						<p>
							<span>Need Help?</span>
							<Link to="/reset-password" tabIndex={5}>Forgot Password</Link>
						</p>
					</FooterLinks>

					{testResellerLabel && (
						<TestEnvLabel>
							Env: <strong>{testResellerLabel}</strong>
						</TestEnvLabel>
					)}
				</FormStack>
			</form>
		);
	}

}
